import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, Trash2, Package, Lightbulb, Pencil, FileText, Loader2, Target, Eye } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { type Campaign } from './types';
import { formatDateShort } from './utils';

interface CondensedCampaignRowProps {
    campaign: Campaign;
    onEdit?: (campaignId: string) => void;
    onView?: (campaignId: string) => void;
    onDelete?: (campaignId: number) => void;
    onGenerateBrief?: (campaign: Campaign) => void;
    isGeneratingBrief?: boolean;
    generatingBriefCampaignId?: number;
    canEdit?: boolean;
    canDelete?: boolean;
}

export function CondensedCampaignRow({
    campaign,
    onEdit,
    onDelete,
    onView,
    onGenerateBrief,
    isGeneratingBrief = false,
    generatingBriefCampaignId,
    canEdit = false,
    canDelete = false
}: CondensedCampaignRowProps) {
    return (
        <div className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
            <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                {campaign.image ? (
                    <ImageWithFallback
                        src={campaign.image}
                        alt={campaign.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Target className="w-6 h-6 text-gray-300" />
                    </div>
                )}
            </div>

            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-gray-900 truncate">{campaign.name}</h4>
                        <p className="text-sm text-gray-500 truncate">
                            {campaign.businessUnit} - {campaign.category}
                        </p>
                    </div>
                    <Badge
                        variant="secondary"
                        className={`flex-shrink-0 text-xs ${campaign.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : campaign.status === 'upcoming'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-gray-100 text-gray-800'
                            }`}
                    >
                        {campaign.status === 'active' ? 'Active' : campaign.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                    </Badge>
                </div>

                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDateShort(campaign.startDate)} - {formatDateShort(campaign.endDate)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        <span>{campaign.products?.length || 0} products</span>
                    </div>
                    {campaign.concept && (
                        <div className="flex items-center gap-1">
                            <Lightbulb className="w-3 h-3 text-purple-600" />
                            <span className="text-purple-700 font-medium truncate max-w-[150px]">{campaign.concept}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-1 flex-shrink-0">
                {onView && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-amber-600 hover:bg-orange-50 hover:text-amber-700"
                        onClick={() => onView(campaign.id.toString())}
                        title="Open showcase page"
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                )}
                {onGenerateBrief && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-amber-600 hover:bg-orange-50 hover:text-amber-700"
                        onClick={() => onGenerateBrief(campaign)}
                        title="Generate campaign brief"
                    >
                        {isGeneratingBrief && generatingBriefCampaignId === campaign.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <FileText className="h-4 w-4" />
                        )}
                    </Button>
                )}

                {canEdit && onEdit && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-blue-500 hover:bg-blue-50 hover:text-blue-700"
                        onClick={() => onEdit(campaign.id.toString())}
                        title="Edit campaign"
                    >
                        <Pencil className="h-4 w-4" />
                    </Button>
                )}

                {canDelete && onDelete && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                        onClick={() => onDelete(campaign.id)}
                        title="Delete campaign"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}
